import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { formatDate, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '~/context/AuthProvider';
import { CampaignListItem } from '~/types/campaigns';
import { getCampaignStatusBadgeClass } from '~/utils/campaignBadgeStyles';
import CampaignActionDropdown from '~/components/CampaignRequestDetails/CampaignActionDropdown';
import {
    fetchCampaignDonorCounts,
    fetchCampaigns,
    fetchCampaignSearchFilters,
    fetchPatientServicesCoordinators,
} from '~/services/campaign';
import AddCampaignDrawer from '~/components/Campaigns/AddCampaignDrawer';
import { useQueries } from '@tanstack/react-query';
import { formatCurrency, ReactMultiSelectInput, ReactSelectInput } from '~/common/form/Inputs';
import { Pagination } from '~/components/Pagination';
import {
    datePresetSelectOptions,
    getDatePresets,
} from '~/utils/datePresets';

const debounce = (
    func: (name: string, value: string) => void,
    delay: number
) => {
    let timeoutId: NodeJS.Timeout;
    return (name: string, value: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(name, value), delay);
    };
};

const columnHelper = createColumnHelper<CampaignListItem>();

const columns = [
    columnHelper.accessor('full_name', {
        header: () => 'Client',
        size: 200,
        cell: (props) => {
            const patient_info = props.row.original.patient_info;

            return (
                <>
                    <Link
                        className='font-bold text-primary'
                        to={`/campaigns/${props.row.original.campaign_id}`}
                    >
                        {props.renderValue()}
                    </Link>
                    <div className={'text-[12px]/[16px] text-[#949599]'}>
                        {patient_info?.location}
                    </div>
                </>
            );
        },
    }),
    columnHelper.accessor('campaign_status', {
        header: () => 'Status',
        size: 110,
        cell: (props) => (
            <div
                className={`inline-flex rounded-full px-3 py-1 text-[12px]/[14.4px] hover:bg-opacity-90 ${getCampaignStatusBadgeClass(
                    props.getValue()
                )}`}
            >
                {props.renderValue()}
            </div>
        ),
    }),
    columnHelper.accessor((row) => row.patient_info?.medical_reason ?? '', {
        id: 'medical_reason',
        header: () => 'Medical Reason',
        size: 150,
        cell: (props) => {
            const medicalReason = props.getValue();
            const medicalIssue = props.row.original.patient_info?.medical_issue;
            return (
                <div>
                    <div>{medicalReason || ''}</div>
                    {medicalIssue && (
                        <div className='text-sm text-secondary'>
                            {medicalIssue}
                        </div>
                    )}
                </div>
            );
        },
    }),
    // columnHelper.accessor(() => 'Need Data', {
    //     id: 'special_circumstance',
    //     header: () => 'Special Circumstance',
    //     cell: (props) => props.renderValue(),
    // }),
    columnHelper.accessor('client_services_coordinator', {
        id: 'csc',
        header: () => 'CSC',
        size: 150,
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor(
        (props) =>
            props.update_date
                ? formatDate(parseISO(props.update_date), 'MM/dd/yy')
                : '',
        {
            id: 'last_activity',
            header: () => 'Last Activity',
            size: 105,
            cell: (props) => props.renderValue(),
        }
    ),
    columnHelper.accessor(
        (props) =>
            props.funded_date
                ? formatDate(parseISO(props.funded_date), 'MM/dd/yy')
                : '',
        {
            id: 'funded_date',
            header: () => 'Open Date',
            size: 105,
            cell: (props) => props.renderValue(),
        }
    ),
    columnHelper.accessor('entire_funds_raised', {
        id: 'entire_funds_raised',
        header: () => 'Donations',
        size: 100,
        cell: (props) => {
            const val = props.getValue();
            if (!val) return <span></span>;
            const num = parseFloat(val);
            return formatCurrency(num);
        },
    }),
    columnHelper.accessor('unique_donor_count', {
        id: 'unique_donors',
        header: () => 'Unique Donors',
        size: 80,
        cell: (props) => props.renderValue(),
    }),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type FilterOption = {
    label: string;
    value: string;
};

type MedicalIssue = {
    id: number;
    name: string;
    medical_reason: {
        id: number;
        name: string;
    };
};

type Filters = {
    status: FilterOption[];
    medical_reason: FilterOption[];
    medical_issues: MedicalIssue[];
    region: FilterOption[];
    verifiedStatus: FilterOption[];
    csc: FilterOption[];
};

type SelectedFilters = {
    name: string;
    status: string | string[];
    medical_reason: string;
    medical_issue: string;
    region: string;
    verifiedStatus: string;
    csc: string;
};

const initialFilters: SelectedFilters = {
    name: '',
    status: 'funded,disbursable',
    medical_reason: '',
    medical_issue: '',
    region: '',
    verifiedStatus: '',
    csc: '',
};

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

const ClientCampaigns: React.FC = () => {
    const { person } = useAuth();
    const location = useLocation();
    const state = location.state as { myCampaigns?: boolean } | null;
    const [pageSize, setPageSize] = useState(20);
    const [pages, setPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [sort, setSort] = useState<Sort | null>(Sort.DESC);
    const [filters, setFilters] = useState<Filters | null>(null);
    const [selectedFilters, setSelectedFilters] =
        useState<SelectedFilters>(() => {
            try {
                const saved = localStorage.getItem('clientCampaignFilters');
                return saved ? { ...initialFilters, ...JSON.parse(saved) } : initialFilters;
            } catch {
                return initialFilters;
            }
        });
    const [clientSearch, setClientSearch] = useState(() => {
        try {
            const saved = localStorage.getItem('clientCampaignFilters');
            return saved ? JSON.parse(saved).name || '' : '';
        } catch {
            return '';
        }
    });
    const [sortBy, setSortBy] = useState<string | null>('last_activity');
    const [dateRange, setDateRange] = useState<DateRange>(() => {
        try {
            const saved = localStorage.getItem('clientCampaignDateRange');
            return saved ? JSON.parse(saved) : initialDateRange;
        } catch {
            return initialDateRange;
        }
    });
    const [isCreateCampaignShowing, setIsCreatedCampaignShowing] =
        useState(false);
    const [rowData, setRowData] = useState<CampaignListItem[]>([]);
    const [showDateFilters, setShowDateFilters] = useState(false);
    const [selectedDatePreset, setSelectedDatePreset] = useState('');
    const datePresets = getDatePresets();

    const debouncedHandleFilter = debounce((name: string, value: string) => {
        setPage(1);
        setSelectedFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, 300);

    const results = useQueries({
        queries: [
            {
                queryKey: [
                    'campaigns',
                    { pageSize, sort, sortBy, page },
                    selectedFilters,
                    dateRange,
                ],
                queryFn: () => {
                    console.log('[ClientCampaigns] Fetching with selectedFilters:', JSON.stringify(selectedFilters));
                    // Map column IDs to actual API field names
                    const sortByFieldMap: Record<string, string> = {
                        'last_activity': 'update_date',
                    };
                    const actualSortBy = sortBy ? (sortByFieldMap[sortBy] || sortBy) : sortBy;
                    return fetchCampaigns(
                        { pageSize, sort, sortBy: actualSortBy },
                        selectedFilters,
                        dateRange,
                        page
                    );
                },
                staleTime: 10000,
            },
            {
                queryKey: ['campaign_search_filters'],
                queryFn: () => fetchCampaignSearchFilters(),
                staleTime: 10000,
            },
            {
                queryKey: ['patient_services_coordinators'],
                queryFn: () => fetchPatientServicesCoordinators(),
                staleTime: 60000,
            },
        ],
    });

    const campaignResults = results[0]?.data;

    useEffect(() => {
        const total = campaignResults?.count;
        if (total) {
            setPages(Math.ceil(total / pageSize));
        }
    }, [campaignResults, pageSize]);

    useEffect(() => {
        try {
            localStorage.setItem('clientCampaignFilters', JSON.stringify(selectedFilters));
        } catch {}
    }, [selectedFilters]);

    useEffect(() => {
        try {
            localStorage.setItem('clientCampaignDateRange', JSON.stringify(dateRange));
        } catch {}
        if (!dateRange.start_date && !dateRange.end_date && selectedDatePreset) {
            setSelectedDatePreset('');
        }
    }, [dateRange, selectedDatePreset]);

    const getDonorCounts = async (ntaf_ids: string[], rows) => {
        if (ntaf_ids.length === 0) return;
        const donorCounts = await fetchCampaignDonorCounts(ntaf_ids);
        const updatedRowData = rows.map((row) => {
            const donorCount = donorCounts.find(
                (donorCount) => donorCount.ntaf_id === row.ntaf_id
            );
            return {
                ...row,
                unique_donor_count: donorCount?.donor_count,
            };
        });
        setRowData(updatedRowData);
    };

    useEffect(() => {
        if (campaignResults) {
            // Get unique donor counts
            const ntaf_ids = campaignResults.results.map(
                (result) => result.ntaf_id
            );
            getDonorCounts(ntaf_ids, campaignResults.results);
            setRowData(campaignResults.results);
        }
    }, [campaignResults]);

    useEffect(() => {
        const filterData = results[1]?.data;
        const pscData = results[2]?.data;
        const verifiedOptions = [
            { label: 'Verified', value: 'verified' },
            { label: 'Unverified', value: 'unverified' },
        ];
        if (filterData && pscData && !filters) {
            // Handle both array and paginated response formats
            const pscList = Array.isArray(pscData) ? pscData : (pscData.results || []);
            
            const newFilters = {
                status: filterData.campaign_statuses.map((status) => ({
                    label: status.name,
                    value: status.code,
                })),
                medical_reason: filterData.medical_reason.map(
                    (reason: { name: string; id: number }) => ({
                        label: reason.name,
                        value: String(reason.id),
                    })
                ),
                medical_issues: filterData.medical_issues || [],
                region: filterData.campaign_regions.map((region) => ({
                    label: region.name,
                    value: region.code,
                })),
                verifiedStatus: verifiedOptions,
                csc: pscList.map((psc: any) => ({
                    label: psc.full_name || '',
                    value: String(psc.id),
                })),
            };
            setFilters(newFilters);

            // Pre-select CSC filter when navigating from "My Campaigns" dashboard card
            if (state?.myCampaigns && person) {
                const fullName = `${person.first_name} ${person.last_name}`;
                console.log('[ClientCampaigns] Looking for CSC:', fullName);
                console.log('[ClientCampaigns] CSC options:', newFilters.csc);
                const myPsc = newFilters.csc.find(
                    (opt: FilterOption) => opt.label === fullName
                );
                console.log('[ClientCampaigns] Found CSC match:', myPsc);
                if (myPsc) {
                    setSelectedFilters((prev) => ({ ...prev, csc: myPsc.value, status: '' }));
                }
            }
        }
    }, [results, filters]);

    const table = useReactTable({
        data: rowData || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const isLoading = results.some((result) => result.isLoading);
    console.log('isLoading: ', isLoading);
    const error = results.find((result) => result.error);

    // if (isLoading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            if (sort === Sort.ASC) {
                setSort(Sort.DESC);
            } else {
                setSort(Sort.ASC);
            }
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleFilter = (filterType: string, value: string) => {
        console.log('handling filter', filterType, value);
        setPage(1);
        // Clear medical_issue when medical_reason changes
        if (filterType === 'medical_reason') {
            setSelectedFilters({
                ...selectedFilters,
                medical_reason: value,
                medical_issue: '',
            });
        } else {
            setSelectedFilters({
                ...selectedFilters,
                [filterType]: value,
            });
        }
    };
    const handleDateRange = (dateRangeType: string, value: string) => {
        setPage(1);
        setDateRange({ ...dateRange, [dateRangeType]: value });
        setSelectedDatePreset('');
    };

    const handleDatePresetChange = (presetValue: string) => {
        setSelectedDatePreset(presetValue);
        const preset = datePresets.find((p) => p.value === presetValue);
        if (preset) {
            const range = preset.getRange();
            setPage(1);
            setDateRange({ start_date: range.start, end_date: range.end });
        }
    };

    const handleRemoveChip = (filterType: string, chipValue?: string) => {
        setPage(1);
        if (filterType === 'status' && chipValue) {
            const currentValues = String(selectedFilters.status).split(',').filter(Boolean);
            const newValues = currentValues.filter((v) => v !== chipValue);
            setSelectedFilters({
                ...selectedFilters,
                status: newValues.join(','),
            });
        } else if (filterType === 'name') {
            setClientSearch('');
            setSelectedFilters({ ...selectedFilters, name: '' });
        } else if (filterType === 'date') {
            setDateRange(initialDateRange);
        } else if (filterType === 'medical_reason') {
            setSelectedFilters({ ...selectedFilters, medical_reason: '', medical_issue: '' });
        } else {
            setSelectedFilters({ ...selectedFilters, [filterType]: '' });
        }
    };

    const handleClearAllFilters = () => {
        setPage(1);
        setSelectedFilters({ ...initialFilters, status: '' });
        setClientSearch('');
        setDateRange(initialDateRange);
        localStorage.removeItem('clientCampaignFilters');
        localStorage.removeItem('clientCampaignDateRange');
    };

    return (
        <>
            <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <h1 className='text-title-md2 font-semibold text-black dark:text-white'>
                    Client Campaigns
                </h1>

                <nav>
                    <ol className='flex items-center gap-2'>
                        <li>
                            <Link to='/'>My Dashboard /</Link>
                        </li>
                        <li className='font-bold text-primary'>Client Campaigns</li>
                    </ol>
                </nav>
            </div>

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-center justify-start gap-4 overflow-x-auto px-8 pb-4'>
                    <div className='relative min-w-[200px]'>
                        <svg
                            className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400'
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                            />
                        </svg>
                        <input
                            type='text'
                            placeholder='Client Name or Email'
                            className='h-[36px] w-full rounded border border-[#E2E8F0] pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none'
                            value={clientSearch}
                            onChange={(e) => {
                                setClientSearch(e.target.value);
                                debouncedHandleFilter('name', e.target.value);
                            }}
                        />
                    </div>
                    {!filters?.status ? (
                        <div className='w-[120px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[200px]'>
                            <ReactMultiSelectInput
                                options={filters?.status || []}
                                value={String(selectedFilters.status || '')}
                                onChange={(value) =>
                                    handleFilter('status', value || initialFilters.status as string)
                                }
                                placeholder='Status'
                            />
                        </div>
                    )}
                    {!filters?.csc ? (
                        <div className='w-[180px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[180px] max-w-[180px]'>
                            <ReactSelectInput
                                options={filters?.csc || []}
                                value={selectedFilters.csc}
                                onChange={(value) =>
                                    handleFilter('csc', value)
                                }
                                placeholder='CSC'
                            />
                        </div>
                    )}
                    {!filters?.medical_reason ? (
                        <div className='w-[180px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[180px]'>
                            <ReactSelectInput
                                options={filters?.medical_reason || []}
                                value={selectedFilters.medical_reason}
                                onChange={(value) =>
                                    handleFilter('medical_reason', value)
                                }
                                placeholder='Medical Reason'
                            />
                        </div>
                    )}
                    {selectedFilters.medical_reason &&
                        filters?.medical_issues && (
                            <div className='relative'>
                                <div className='min-w-[175px]'>
                                    <ReactSelectInput
                                        options={filters.medical_issues
                                            .filter(
                                                (issue) =>
                                                    issue.medical_reason &&
                                                    String(issue.medical_reason.id) ===
                                                    String(selectedFilters.medical_reason)
                                            )
                                            .map((issue) => ({
                                                label: issue.name,
                                                value: String(issue.id),
                                            }))}
                                        value={selectedFilters.medical_issue}
                                        onChange={(value) =>
                                            handleFilter('medical_issue', value)
                                        }
                                        placeholder='Medical Issue'
                                    />
                                </div>
                            </div>
                        )}
                    {!filters?.region ? (
                        <div className='w-[175px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[175px] max-w-[175px]'>
                            <ReactSelectInput
                                options={filters?.region || []}
                                value={selectedFilters.region}
                                onChange={(value) =>
                                    handleFilter('region', value)
                                }
                                placeholder='Region'
                            />
                        </div>
                    )}
                    {!filters?.verifiedStatus ? (
                        <div className='w-[175px] animate-pulse'>
                            <div className='h-4 rounded-full bg-gray-4 dark:bg-gray-5'></div>
                        </div>
                    ) : (
                        <div className='min-w-[175px] max-w-[175px]'>
                            <ReactSelectInput
                                options={filters?.verifiedStatus || []}
                                value={selectedFilters.verifiedStatus}
                                onChange={(value) =>
                                    handleFilter('verifiedStatus', value)
                                }
                                placeholder='Verified Status'
                            />
                        </div>
                    )}
                    <button
                        type='button'
                        onClick={() => setShowDateFilters(!showDateFilters)}
                        className={`flex h-[36px] items-center gap-1.5 rounded border px-3 text-sm transition-colors ${
                            showDateFilters || dateRange.start_date || dateRange.end_date
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-[#E2E8F0] text-gray-600 hover:border-primary hover:text-primary'
                        }`}
                    >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={1.5}
                            stroke='currentColor'
                            className='h-4 w-4'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                d='M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'
                            />
                        </svg>
                        {selectedFilters.status ? 'Status Date' : 'Created Date'}
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            fill='none'
                            viewBox='0 0 24 24'
                            strokeWidth={2}
                            stroke='currentColor'
                            className={`h-3 w-3 transition-transform ${showDateFilters ? 'rotate-180' : ''}`}
                        >
                            <path strokeLinecap='round' strokeLinejoin='round' d='m19.5 8.25-7.5 7.5-7.5-7.5' />
                        </svg>
                    </button>
                </div>

                {/* Active filter chips */}
                {(() => {
                    const chips: { label: string; filterType: string; chipValue?: string }[] = [];

                    // Status chips
                    const statusValues = String(selectedFilters.status || '').split(',').filter(Boolean);
                    statusValues.forEach((sv) => {
                        const opt = filters?.status?.find((s) => s.value === sv);
                        chips.push({ label: `Status: ${opt?.label || sv}`, filterType: 'status', chipValue: sv });
                    });

                    // Single-value filters
                    if (selectedFilters.csc) {
                        const opt = filters?.csc?.find((o) => String(o.value) === selectedFilters.csc);
                        chips.push({ label: `CSC: ${opt?.label || selectedFilters.csc}`, filterType: 'csc' });
                    }
                    if (selectedFilters.medical_reason) {
                        const opt = filters?.medical_reason?.find((o) => String(o.value) === selectedFilters.medical_reason);
                        chips.push({ label: `Medical Reason: ${opt?.label || selectedFilters.medical_reason}`, filterType: 'medical_reason' });
                    }
                    if (selectedFilters.medical_issue) {
                        const issue = filters?.medical_issues?.find((o) => String(o.id) === selectedFilters.medical_issue);
                        chips.push({ label: `Medical Issue: ${issue?.name || selectedFilters.medical_issue}`, filterType: 'medical_issue' });
                    }
                    if (selectedFilters.region) {
                        const opt = filters?.region?.find((o) => String(o.value) === selectedFilters.region);
                        chips.push({ label: `Region: ${opt?.label || selectedFilters.region}`, filterType: 'region' });
                    }
                    if (selectedFilters.verifiedStatus) {
                        const opt = filters?.verifiedStatus?.find((o) => String(o.value) === selectedFilters.verifiedStatus);
                        chips.push({ label: `Verified: ${opt?.label || selectedFilters.verifiedStatus}`, filterType: 'verifiedStatus' });
                    }

                    // Name search
                    if (selectedFilters.name) {
                        chips.push({ label: `Name: "${selectedFilters.name}"`, filterType: 'name' });
                    }

                    // Date range
                    if (dateRange.start_date || dateRange.end_date) {
                        const start = dateRange.start_date || '...';
                        const end = dateRange.end_date || '...';
                        chips.push({ label: `Date: ${start} - ${end}`, filterType: 'date' });
                    }

                    if (chips.length === 0) return null;

                    return (
                        <div className='flex flex-wrap items-center gap-2 px-8 pb-3'>
                            {chips.map((chip, i) => (
                                <span
                                    key={`${chip.filterType}-${chip.chipValue || i}`}
                                    className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700'
                                >
                                    {chip.label}
                                    <button
                                        type='button'
                                        onClick={() => handleRemoveChip(chip.filterType, chip.chipValue)}
                                        className='ml-0.5 text-gray-400 hover:text-gray-700'
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <button
                                type='button'
                                onClick={handleClearAllFilters}
                                className='text-xs text-gray-500 hover:text-primary'
                            >
                                Clear all
                            </button>
                        </div>
                    );
                })()}

                {showDateFilters && (
                    <div className='flex flex-wrap items-center gap-4 px-8 pb-4'>
                        <span className='text-sm text-gray-600'>
                            {selectedFilters.status ? 'Filter by status date:' : 'Filter by created date:'}
                        </span>
                        <div className='min-w-[180px]'>
                            <ReactSelectInput
                                options={datePresetSelectOptions}
                                value={selectedDatePreset}
                                onChange={handleDatePresetChange}
                                placeholder='Select date range'
                            />
                        </div>
                        <input
                            type='date'
                            name='start_date'
                            className='rounded border border-[#E2E8F0] p-[6px]'
                            value={dateRange.start_date}
                            onChange={(event) =>
                                handleDateRange('start_date', event.target.value)
                            }
                        />
                        <span className='text-sm text-gray-500'>to</span>
                        <input
                            type='date'
                            name='end_date'
                            className='rounded border border-[#E2E8F0] p-[6px]'
                            value={dateRange.end_date}
                            onChange={(event) =>
                                handleDateRange('end_date', event.target.value)
                            }
                        />
                        {(dateRange.start_date || dateRange.end_date) && (
                            <button
                                type='button'
                                onClick={() => {
                                    setDateRange(initialDateRange);
                                    setPage(1);
                                }}
                                className='text-sm text-gray-500 hover:text-danger'
                            >
                                Clear dates
                            </button>
                        )}
                    </div>
                )}

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing {Math.min(pageSize, (campaignResults?.count || 0) - (page - 1) * pageSize)} of {campaignResults?.count || 0} campaigns
                    </p>

                    {/* <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineEdit className='text-primary' size={18} />
                            Edit
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdMerge className='text-primary' size={18} />
                            Merge
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdClose className='text-red' size={18} />
                            Deactivate
                        </div>
                    </div> */}
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-fixed border-collapse break-words px-4 md:px-8'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => {
                                        const useChevron =
                                            header.column.id === sortBy;
                                        let chevron = null;
                                        if (useChevron) {
                                            if (sort === Sort.ASC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            } else if (sort === Sort.DESC) {
                                                chevron = (
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        viewBox='0 0 20 20'
                                                        fill='currentColor'
                                                        className='h-5 w-5'
                                                    >
                                                        <path
                                                            fillRule='evenodd'
                                                            d='M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z'
                                                            clipRule='evenodd'
                                                        />
                                                    </svg>
                                                );
                                            }
                                        }

                                        return (
                                            <th
                                                style={{
                                                    paddingTop: '16px',
                                                    paddingBottom: '16px',
                                                    width: header.getSize(),
                                                }}
                                                key={header.id}
                                            >
                                                <div className='flex items-center'>
                                                    <span
                                                        onClick={() =>
                                                            handleSort(
                                                                header.column.id
                                                            )
                                                        }
                                                        className='cursor-pointer'
                                                    >
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(
                                                                  header.column
                                                                      .columnDef
                                                                      .header,
                                                                  header.getContext()
                                                              )}
                                                    </span>
                                                    <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                        <span className='inline-block'>
                                                            {chevron}
                                                        </span>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th
                                        style={{
                                            width: '70px',
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              className='text-right'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          >
                                              <CampaignActionDropdown
                                                  campaignId={
                                                      row?.original?.campaign_id
                                                  }
                                              />
                                          </td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination page={page} setPage={setPage} pages={pages} totalCount={campaignResults?.count} pageSize={pageSize} />
            </section>
            <AddCampaignDrawer
                isOpen={isCreateCampaignShowing}
                setIsOpen={setIsCreatedCampaignShowing}
            />
        </>
    );
};

export default ClientCampaigns;
